import { useEffect } from "react";
import { motion } from "framer-motion";
import { GlassWater, Factory, Truck, ShoppingBag, Coffee, Store, Snowflake, Sparkles, Leaf, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useQuote } from "./Header";
import { SITE } from "../../lib/config";
import { track } from "../../lib/analytics";

const APPLICATIONS = [
  { icon: GlassWater, t: "Juice Shops & Bars" },
  { icon: Sparkles, t: "Cold-Pressed Juice Brands" },
  { icon: Coffee, t: "Cafes & Smoothie Bars" },
  { icon: Store, t: "Retail & Supermarkets" },
  { icon: Truck, t: "Beverage Delivery" },
  { icon: Snowflake, t: "Dairy & Milkshake Brands" },
  { icon: Factory, t: "Beverage Manufacturers" },
  { icon: ShoppingBag, t: "Cloud Kitchens & QSRs" },
  { icon: Leaf, t: "Health & Wellness Brands" },
];

const STANDARD_CAPS = ["200 ml", "250 ml", "300 ml", "350 ml", "500 ml", "700 ml", "750 ml", "1000 ml"];

const SHAPES = [
  {
    label: "Square Juice PET Bottles",
    short: "Square",
    img: "/juice-shapes/square.jpg",
    tag: "Premium shelf appeal",
    desc: "Modern square-shape PET juice bottles that stack neatly and stand out on retail shelves and delivery packs.",
    uses: "Cold-pressed juice • Shakes • Health drinks",
    caps: STANDARD_CAPS,
  },
  {
    label: "Round Juice PET Bottles",
    short: "Round",
    img: "/juice-shapes/round.jpg",
    tag: "Classic beverage bottle",
    desc: "Traditional round PET juice bottles — the go-to choice for juice shops, cafes and beverage brands.",
    uses: "Fresh juice • Lassi • Mocktails",
    caps: STANDARD_CAPS,
  },
  {
    label: "Sugarcane Juice PET Bottles",
    short: "Sugarcane",
    img: "/juice-shapes/sugarcane.jpg",
    tag: "Sugarcane juice specials",
    desc: "Purpose-designed PET bottles for sugarcane juice — leak-proof, tamper-evident and ready for cold storage.",
    uses: "Sugarcane juice • Fresh cane blends",
    caps: ["300 ml", "500 ml"],
  },
  {
    label: "Star Shape Juice PET Bottles",
    short: "Star",
    img: "/juice-shapes/star.jpg",
    tag: "Designer bottle",
    desc: "Distinctive star-shape PET bottles that make juices, mocktails and premium beverages look instantly premium.",
    uses: "Premium juices • Mocktails • Gifting",
    caps: ["375 ml"],
  },
];

const TYPES = [
  "Juice PET bottles with caps",
  "Cold-pressed juice bottles",
  "Sugarcane juice bottles",
  "Milkshake & smoothie bottles",
  "Tamper-evident PET bottles",
  "Custom-shape juice bottles",
];

const FAQS = [
  { q: "What are juice PET bottles used for?",
    a: "Juice PET bottles are used by juice shops, cold-pressed juice brands, cafes, cloud kitchens and beverage manufacturers to pack fresh juices, sugarcane juice, milkshakes, smoothies and other cold beverages for takeaway, retail and delivery." },
  { q: "Do you supply juice PET bottles in wholesale quantities?",
    a: "Yes. Chennai Packs supplies juice PET bottles in wholesale/bulk quantities to juice brands, cafes, cloud kitchens and beverage businesses. Share your required shape, size and quantity for pricing." },
  { q: "What shapes and sizes of juice PET bottles are available?",
    a: "Our juice PET bottle range includes square, round, sugarcane and star-shape designs across multiple capacities suitable for cold-pressed juice, sugarcane juice, shakes and other beverages. Enquire with your required capacity for exact availability." },
  { q: "Do juice PET bottles come with caps?",
    a: "Yes, caps are available for the juice PET bottles in our range. Tamper-evident cap options can be confirmed when you request a quote." },
  { q: "Are these bottles food-grade and suitable for juice?",
    a: "Yes. Our juice PET bottles are food-grade, suitable for cold beverages including fresh juices, sugarcane juice, milkshakes and smoothies." },
  { q: "Do you supply juice bottles to cold-pressed juice brands and cafes?",
    a: "Yes. Cold-pressed juice brands, juice shops, cafes and cloud kitchens are among our regular customers for juice PET bottles and beverage packaging." },
  { q: "Do you supply juice PET bottles in Chennai?",
    a: "Yes. Chennai Packs is a Chennai-based supplier of juice PET bottles, serving customers across Chennai, Tamil Nadu and pan-India." },
  { q: "How can I get a wholesale quote?",
    a: "Click ‘Get a Quote’ or ‘Enquire on WhatsApp’ on this page, share your required bottle shape, size and quantity, and our team will respond with wholesale pricing." },
];

export default function JuiceBottlesContent() {
  const openQuote = useQuote();
  const waHref = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Chennai Packs, I'd like a quote for Juice PET Bottles.")}`;

  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-juice-faq", "1");
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(s);
    return () => {
      document.head.querySelectorAll('script[data-juice-faq="1"]').forEach((n) => n.remove());
    };
  }, []);

  return (
    <>
      {/* Intro */}
      <section className="py-16" data-testid="juice-intro">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Juice PET Bottles for Beverage Businesses
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies food-grade juice PET bottles for juice shops, cold-pressed
            juice brands, cafes, cloud kitchens and beverage manufacturers. Our juice bottle range
            is available in multiple shapes and sizes for packing fresh juices, sugarcane juice,
            milkshakes, smoothies and other cold beverages.
          </p>
        </div>
      </section>

      {/* Shapes */}
      <section className="py-16 bg-[#F8FAF8]" data-testid="juice-shapes">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Juice PET Bottle Shapes
          </h2>
          <p className="text-[#3A4D3E] mt-3 max-w-2xl">
            Our juice PET bottle range is stocked in multiple shapes to match every beverage brand
            — from classic round bottles for juice shops to designer star bottles for premium
            packaging. All shapes are available with tamper-evident caps on request.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SHAPES.map((s) => (
              <article
                key={s.label}
                className="bg-white border border-[#E2E8E4] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
                data-testid={`juice-shape-${s.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              >
                <div className="aspect-[4/3] bg-[#F8FAF8] overflow-hidden">
                  <img
                    src={s.img}
                    alt={`${s.label} — juice PET bottle from Chennai Packs`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#1D6B2A]">
                    {s.tag}
                  </span>
                  <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-1">
                    {s.label}
                  </h3>
                  <p className="text-sm text-[#3A4D3E] mt-2 leading-relaxed">{s.desc}</p>
                  <p className="text-xs text-[#0A1C0E] mt-3 font-medium">{s.uses}</p>
                  <div className="mt-4">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-[#3A4D3E] mb-2">
                      Tap a size to WhatsApp us
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.caps.map((cap) => {
                        const waMsg = `Hi Chennai Packs, I'd like a quote for Juice PET Bottles — ${s.short} ${cap}. Please share pricing and MOQ. Thank you.`;
                        const waUrl = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(waMsg)}`;
                        return (
                          <a
                            key={cap}
                            href={waUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => track("Juice Size WhatsApp", { shape: s.short, size: cap })}
                            data-testid={`juice-cap-${s.short.toLowerCase()}-${cap.replace(/\s+/g, '').toLowerCase()}`}
                            title={`WhatsApp us for ${s.short} ${cap}`}
                            className="inline-flex items-center gap-1 border border-[#E2E8E4] bg-[#F8FAF8] hover:bg-[#1D6B2A] hover:text-white hover:border-[#1D6B2A] text-[#0A1C0E] rounded-full px-3 py-1 text-xs font-semibold transition-colors"
                          >
                            {cap}
                            <MessageCircle size={12} className="opacity-80" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => openQuote({ name: `Juice PET Bottles — ${s.label}`, sku: "CP-014" })}
                    data-testid={`juice-shape-quote-${s.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                    className="mt-5 w-full bg-[#1D6B2A] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#154d1e] transition-colors"
                  >
                    Get a Quote for {s.short}
                  </button>
                </div>
              </article>
            ))}
          </div>
          <p className="text-sm text-[#3A4D3E] mt-8 text-center">
            Share your required bottle shape, capacity and quantity — our team will confirm exact
            stock, cap options and wholesale pricing.
          </p>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16" data-testid="juice-applications">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Ideal for Beverage Brands & Juice Businesses
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {APPLICATIONS.map((a) => (
              <div key={a.t} className="bg-white border border-[#E2E8E4] rounded-2xl p-5 flex items-center gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#F8FAF8] text-[#1D6B2A] shrink-0">
                  <a.icon size={22} />
                </span>
                <span className="font-head font-semibold text-[#0A1C0E]">{a.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="py-16 bg-[#F8FAF8]" data-testid="juice-types">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Juice PET Bottle Options
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TYPES.map((t) => (
              <div key={t} className="bg-white border border-[#E2E8E4] rounded-xl px-5 py-4 text-[#0A1C0E]">
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wholesale */}
      <section className="py-16" data-testid="juice-wholesale">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Wholesale Juice PET Bottles in Chennai
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies juice PET bottles to juice shops, cold-pressed juice brands,
            cafes, cloud kitchens and beverage manufacturers in Chennai. Enquire with your
            required shape, capacity and quantity for availability and wholesale pricing.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={() => openQuote({ name: "Juice PET Bottles", sku: "CP-014" })}
              data-testid="juice-wholesale-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Custom Branding */}
      <section className="py-16 bg-[#F8FAF8]" data-testid="juice-branding">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Custom Branded Juice Bottles
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Eligible juice PET bottles can be considered for custom labels, shrink sleeves and
            branded packaging with your logo and artwork — a simple way to build brand recall on
            every bottle. Custom-branding availability depends on the specific bottle shape, size
            and minimum order quantity. Share your requirement and our team will confirm whether
            custom branding is possible for the bottles you need.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16" data-testid="juice-faq">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3 mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`juice-${i}`} className="bg-white border border-[#E2E8E4] rounded-2xl px-6 data-[state=open]:shadow-md transition-shadow">
                <AccordionTrigger className="font-head text-base font-semibold text-[#0A1C0E] hover:no-underline text-left py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3A4D3E] leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12 bg-[#F8FAF8]" data-testid="juice-related-links">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h3 className="font-head text-xl font-semibold text-[#0A1C0E]">Related packaging you may need</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { to: "/products/sipper-glasses", label: "Sipper Glasses" },
              { to: "/products/paper-cups", label: "Paper Cups" },
              { to: "/products/pp-biriyani-containers", label: "PP Food Containers" },
              { to: "/products/paper-straw", label: "Paper Straws" },
              { to: "/products?category=plastic-pp", label: "Plastic & PP Ware" },
            ].map((l) => (
              <a key={l.to} href={l.to} className="border border-[#E2E8E4] bg-white rounded-full px-5 py-2 text-sm font-medium text-[#1D6B2A] hover:bg-[#F0F4F0]">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16" data-testid="juice-final-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]"
          >
            Looking for Juice PET Bottles in Chennai?
          </motion.h2>
          <p className="text-lg text-[#3A4D3E] mt-4 max-w-2xl mx-auto">
            Tell us your required bottle shape, capacity and quantity. Enquire with Chennai Packs
            for availability and wholesale pricing.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => openQuote({ name: "Juice PET Bottles", sku: "CP-014" })}
              data-testid="juice-final-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
